<template>
  <div class="assessment-table-container">
    <div class="header-container">
      <div class="left-actions">
        <el-button type="text" @click="goBack" class="back-button">
          <i class="el-icon-arrow-left"></i> 返回
        </el-button>
        <h2 class="title">{{ ruleName }} - 考核表</h2>
      </div>
    </div>

    <!-- 已隐藏：+从模板库中添加 -->
    <div class="indicator-actions" style="margin-bottom: 10px">
      <!-- 已隐藏原模板库按钮 -->
      <div style="display: flex; align-items: center"></div>
      <div>
        <div class="right-actions">
          <el-button
            v-if="canModifyCurrentRule"
            type="primary"
            @click="saveData"
            class="save-button"
            size="small"
          >
            <i class="el-icon-check"></i>
            {{
              isLoadingFromTemplate && !isSuperUser ? "存为我的规则" : "保存"
            }}
          </el-button>
          <el-button v-if="false" type="primary" @click="createFromTemplate" size="small">
            <i class="el-icon-folder-add"></i> 批量导入
          </el-button>

        </div>
      </div>
    </div>

    <el-alert
      v-if="showReadonlyNotice"
      class="readonly-alert"
      title="该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。"
      type="warning"
      show-icon
      :closable="false"
    />

    <div class="mobile-container" v-if="isMobile" style="margin-bottom: 10px">
      <RuleInput
        :rules="sortedTableData"
        :category-options="selectableCategoryOptions"
        :is-super-user="isSuperUser"
        :readonly="!canModifyCurrentRule"
        @update-rules="handleMobileRulesUpdate"
        @category-change="handleMobileCategoryChange"
        @delete-rule="handleDelete"
      ></RuleInput>
    </div>

    <div class="table-container" v-if="!isMobile">
      <el-table
        :data="sortedTableData"
        style="width: 100%"
        height="calc(100vh - 220px)"
        border
      >
        <el-table-column
          prop="category"
          :label="ASSESSMENT_FIELD_LABELS[ASSESSMENT_FIELDS.NAME]"
        >
          <template slot-scope="scope">
            <div class="category-cell">
              <div
                class="category-select-wrapper"
                v-if="!isFixedDimension(scope.row)"
              >
                <el-select
                  v-model="scope.row.categoryId"
                  filterable
                  :allow-create="isSuperUser"
                  default-first-option
                  placeholder="请选择关键指标"
                  class="category-select"
                  @change="handleCategoryChange(scope.row, $event)"
                  :disabled="isFixedDimension(scope.row) || !isSuperUser || !canModifyCurrentRule"
                >
                  <el-option
                    v-for="item in selectableCategoryOptions"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  >
                    <span>{{ item.name }}</span>
                    <el-tag
                      v-if="item.isAiItem && isAiRatingFeatureEnabled"
                      size="mini"
                      type="warning"
                      style="margin-right: 1rem"
                      >AI</el-tag
                    >
                  </el-option>
                </el-select>
                <el-tag
                  v-if="
                    isCategoryAi(scope.row.categoryId) &&
                    isAiRatingFeatureEnabled
                  "
                  size="mini"
                  type="warning"
                  class="selected-ai-tag"
                  >AI</el-tag
                >
              </div>
              <div v-else class="fixed-dimension-display">
                {{ scope.row.categoryName }}
                <el-tag
                  v-if="
                    isCategoryAi(scope.row.categoryId) &&
                    isAiRatingFeatureEnabled
                  "
                  size="mini"
                  type="warning"
                  class="selected-ai-tag"
                  >AI</el-tag
                >
                <el-tag
                  v-if="isFixedDimension(scope.row)"
                  size="mini"
                  type="info"
                  class="fixed-dimension-tag"

                  style="right: 60px"
                  >
                    <i class="el-icon-lock"></i>
                  </el-tag
                >
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- description -->
        <el-table-column
          prop="description"
          :label="ASSESSMENT_FIELD_LABELS[ASSESSMENT_FIELDS.DESCRIPTION]"
          width="300"
        >
          <template slot-scope="scope">
            <el-input
              v-if="!isFixedDimension(scope.row)"
              v-model="scope.row.description"
              placeholder="请输入本月工作目标/计划"
              @dblclick.native="expandInput(scope, 'description')"
              ref="descInput"
              type="textarea"
              autosize
              class="editable-input"
              :disabled="isFixedDimension(scope.row) || !canModifyCurrentRule"
            >
            </el-input>
            <div v-else class="fixed-dimension-display">
              {{ scope.row.description }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="standard"
          :label="ASSESSMENT_FIELD_LABELS[ASSESSMENT_FIELDS.STANDARD]"
        >
          <template slot-scope="scope">
            <el-input
              v-if="!isFixedDimension(scope.row)"
              v-model="scope.row.standard"
              placeholder="请输入标准内容"
              @dblclick.native="expandInput(scope, 'standard')"
              ref="standardInput"
              type="textarea"
              autosize
              class="editable-input"
              :disabled="isFixedDimension(scope.row) || !canModifyCurrentRule"
            >
            </el-input>
            <div v-else class="fixed-dimension-display">
              {{ scope.row.standard }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="weight"
          :label="ASSESSMENT_FIELD_LABELS[ASSESSMENT_FIELDS.WEIGHT]"
        >
          <template slot-scope="scope">
            <el-input-number
              v-if="!isFixedDimension(scope.row)"
              v-model="scope.row.weight"
              :min="0"
              controls-position="right"
              placeholder="请输入单项分值"
              :disabled="isFixedDimension(scope.row) || !isSuperUser || !canModifyCurrentRule"
            ></el-input-number>
            <div v-else class="fixed-dimension-display">
              {{ scope.row.weight }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="dataSource" label="数据源" width="180">
          <template slot-scope="scope">
            <span v-if="scope.row.dataSource">{{
              getDataSourceDisplayName(scope.row.dataSource)
            }}</span>
            <div
              v-else
              style="width: 100%; display: flex; justify-content: center"
            >
              -
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="ASSESSMENT_FIELD_LABELS[ASSESSMENT_FIELDS.ACTION]"
          width="150"
        >
          <template slot-scope="scope">
            <el-button
              type="text"
              @click="handleDelete(scope.$index, scope.row)"
              :disabled="!isRootUser || isFixedDimension(scope.row) || isLoadingFromTemplate || !canModifyCurrentRule"
            >
              <img
                src="@/assets/delete-icon.png"
                class="action-icon"
                alt="删除"
                :style="{ opacity: (!isRootUser || isFixedDimension(scope.row) || isLoadingFromTemplate || !canModifyCurrentRule) ? 0.5 : 1 }"
              />
            </el-button>
          </template>
        </el-table-column>
        <!-- Append slot for Add button at the end of table -->
        <template slot="append">
          <div
            v-if="isRootUser && canModifyCurrentRule"
            class="table-append-row"
            style="padding: 12px 0; text-align: center;"
          >
            <el-button
              type="primary"
              @click="addCustomIndicator"
              icon="el-icon-plus"
              size="small"
            >
              添加自定义指标
            </el-button>
          </div>
        </template>
      </el-table>
    </div>
    <!-- 批量导入对话框 -->
    <el-dialog
      title="批量导入"
      :visible.sync="batchImportDialogVisible"
      width="40%"
      :fullscreen="isMobile"
      append-to-body
    >
      <div class="import-dialog">
        <div class="import-instruction">
          <p>1、下载导入模板</p>
          <p class="tip">根据提示信息完善表格内容</p>
          <el-button
            type="primary"
            size="small"
            @click="downloadTemplate"
            class="download-btn"
          >
            下载空的模板表格
          </el-button>
        </div>

        <div class="upload-section">
          <p>2、上传完善后的表格</p>
          <el-upload
            class="upload-demo"
            action="#"
            ref="upload"
            :limit="1"
            :before-upload="beforeUpload"
            :on-change="handleFileChange"
            drag
            :auto-upload="false"
            :file-list="fileList"
            accept=".xls,.xlsx"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              可直接将文件拖拽到此处进行上传
              <br />支持格式：XLS、XLSX，文件大小不超过3M
            </div>
          </el-upload>
        </div>

        <div slot="footer" class="dialog-footer">
          <el-button @click="batchImportDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmImport">确认导入</el-button>
        </div>
      </div>
    </el-dialog>
    <!-- 存为模板弹窗 -->
    <el-dialog
      title="保存为模板"
      :visible.sync="saveAsTemplateDialogVisible"
      :fullscreen="isMobile"
      width="50%"
      append-to-body
    >
      <el-form
        ref="templateForm"
        :model="templateForm"
        :rules="templateFormRules"
        label-width="100px"
      >
        <el-form-item label="模板名称" prop="name">
          <el-input v-model="templateForm.name" placeholder="请输入模板名称">
          </el-input>
        </el-form-item>
        <el-form-item label="模板描述" prop="description">
          <el-input
            v-model="templateForm.description"
            placeholder="请输入模板描述"
            type="textarea"
            autosize
          >
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="saveAsTemplateDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSaveAsTemplate"
          >确认保存</el-button
        >
      </div>
    </el-dialog>
    <!-- 模板库导入弹窗 -->
    <el-dialog
      title="考核指标模板库"
      :visible.sync="templateDialogVisible"
      width="50%"
      :fullscreen="isMobile"
      append-to-body
    >
      <div class="template-library">
        <!-- 模板搜索框 -->
        <el-input
          v-model="tempParams.name"
          placeholder="搜索模板名称"
          prefix-icon="el-icon-search"
          @change="getTemplateList"
          style="margin-bottom: 15px"
        ></el-input>
        <!-- 模板表格 -->
        <el-table
          :data="templateData"
          style="width: 100%"
          prop="id"
          border
          highlight-current-row
          @row-dblclick="confirmAddFromTemplate"
          @current-change="handleTemplateSelection"
        >
          <el-table-column
            prop="name"
            label="模板名称"
            width="300"
          ></el-table-column>
          <el-table-column
            prop="description"
            label="模板描述"
            width="500"
          ></el-table-column>
        </el-table>

        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="tempParams.pageNum"
          :page-sizes="[5, 10]"
          :page-size="tempParams.pageSize"
          :total="tempParams.total"
          layout="total, sizes, prev, pager, next, jumper"
          style="margin-top: 15px; text-align: right"
        ></el-pagination>

        <!-- 弹窗底部按钮 -->
        <div slot="footer" class="dialog-footer">
          <el-button @click="templateDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmAddFromTemplate"
            >确认添加</el-button
          >
        </div>
      </div>
    </el-dialog>
    <!-- 克隆规则命名弹窗 -->
    <el-dialog
      title="创建新规则"
      :visible.sync="cloneRuleDialogVisible"
      :fullscreen="isMobile"
      width="40%"
      append-to-body
    >
      <el-form
        ref="cloneRuleForm"
        :model="cloneRuleForm"
        :rules="cloneRuleFormRules"
        label-width="100px"
      >
        <el-form-item label="规则名称" prop="name">
          <el-input v-model="cloneRuleForm.name" placeholder="请输入新规则名称">
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="cloneRuleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmCloneRule">确认创建</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  ASSESSMENT_FIELDS,
  ASSESSMENT_FIELD_LABELS,
} from "@/constants/AssessmentField";
import { isEditableFlag } from "@/utils/rulePermission";
import CategoryType from "@/constants/CategoryType";
import { responsiveMixin } from "@/mixins/responsiveMixin";
import { FEATURE_FLAG_KEYS } from "@/request/feature_flag_request";
import fixedDimensionRequest from "@/request/fixed_dimension_request";
import {
  addDimensionCategory,
  saveDimension as apiSaveDimension,
  listDimensionCategories,
} from "@/request/performance_request";
import { keyBy } from "lodash";
import { mapGetters } from "vuex";
import RuleInput from "./AssessmentTable/RuleInput.vue";

// axios已在main.js中全局配置，使用this.$http代替直接导入的axios，确保token自动添加
export default {
  name: "AssessmentTable",
  mixins: [responsiveMixin],
  components: {
    RuleInput,
  },
  data() {
    return {
      CategoryType,
      ruleId: null,
      ASSESSMENT_FIELDS,
      ASSESSMENT_FIELD_LABELS,
      batchImportDialogVisible: false,
      fileList: [], // 上传的文件列表
      ruleName: "",
      pageParams: { pageNum: 1, pageSize: 10, total: 0 },
      tempParams: { pageNum: 1, pageSize: 5, name: "", total: 0 },
      units: ["%", "元", "万元"],
      tableData: [],
      saveAsTemplateDialogVisible: false,
      templateForm: {
        name: "", // 模板名称
        description: "", // 模板描述
      },
      templateFormRules: {
        name: [{ required: true, message: "请输入模板名称", trigger: "blur" }],
      },
      templateDialogVisible: false, // 模板库弹窗显示状态
      templateData: [], // 模板库原始数据
      selectedTemplates: [], // 选中的模板数据
      queryTempId: 0,
      expandedInputs: {},
      cloneRuleDialogVisible: false, // 克隆规则弹窗显示状态
      cloneRuleForm: {
        name: "", // 新规则名称
      },
      cloneRuleFormRules: {
        name: [{ required: true, message: "请输入规则名称", trigger: "blur" }],
      },
      isLoadingFromTemplate: false, // 是否正在从模板加载
      categoryOptions: [], // All categories for display
      isRuleEditable: false,
      dataSourceTypes: [], // 数据源类型列表
      draftRestoring: false,
      draftLoading: false,
      draftSaveTimer: null,
      permissionLoaded: false,
    };
  },
  computed: {
    role() {
      return this.$store.getters['user/getUserRole'] || "";
    },
    ...mapGetters('featureFlags', [
      'getFeatureFlagsState'
    ]),
    isSuperUser() {
      return this.role === "super";
    },
    isRootUser() {
      return this.$store.getters['user/getUserName'] === "root";
    },
    isAiRatingFeatureEnabled() {
      return this.getFeatureFlagsState[FEATURE_FLAG_KEYS.AI_RATING_ENABLED] === true;
    },
    canModifyCurrentRule() {
      return (this.isLoadingFromTemplate && !this.isSuperUser) || this.isRuleEditable;
    },
    showReadonlyNotice() {
      return this.permissionLoaded && !this.canModifyCurrentRule;
    },
    selectableCategoryOptions() {
      // Only show NON_FIXED categories for user selection in assessment rules
      // FIXED categories should not be selectable in assessment rules
      return this.categoryOptions.filter(
        (cat) => cat.categoryType === CategoryType.NON_FIXED,
      );
    },
    sortedTableData() {
      // Sort table data: fixed dimensions first, then non-fixed dimensions
      // Latest added non-fixed items will be at the bottom
      const fixedItems = [];
      const nonFixedItems = [];

      this.tableData.forEach(item => {
        if (this.isFixedDimension(item)) {
          fixedItems.push(item);
        } else {
          nonFixedItems.push(item);
        }
      });

      // Combine: fixed items first, then non-fixed items
      // Non-fixed items maintain their original order (latest at bottom)
      return [...fixedItems, ...nonFixedItems];
    },
  },
  watch: {
    tableData: {
      deep: true,
      handler() {
        this.queueSaveRuleDraft();
      },
    },
  },
  async created() {
    // Ensure feature flags are initialized before proceeding
    if (!this.$store.getters['featureFlags/areFeatureFlagsInitialized']) {
      try {
        await this.$store.dispatch('featureFlags/initFeatureFlags');
      } catch (error) {
        console.error('Failed to initialize feature flags:', error);
      }
    }

    this.ruleId = this.$route.params.ruleId;
    this.ruleName = this.$route.params.ruleName;
    this.loadCategoryOptions();
    this.loadTableData();
    this.loadDataSourceTypes();
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.saveRuleDraftNow);
  },
  beforeDestroy() {
    this.saveRuleDraftNow();
    if (this.draftSaveTimer) {
      clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.saveRuleDraftNow);
  },
  methods: {
    getCurrentDraftUserId() {
      return this.$store.getters['user/getUserId'] || localStorage.getItem('userId') || "anonymous";
    },
    getRuleDraftKey() {
      return `assessment-rule-draft:${this.getCurrentDraftUserId()}:${this.ruleId || "new"}`;
    },
    getLegacyRuleDraftKey() {
      return `assessment-rule-draft:${this.ruleId || "new"}`;
    },
    queueSaveRuleDraft() {
      if (this.draftRestoring || this.draftLoading || !this.tableData.length) {
        return;
      }
      if (this.draftSaveTimer) {
        clearTimeout(this.draftSaveTimer);
      }
      this.draftSaveTimer = setTimeout(() => {
        this.saveRuleDraftNow();
      }, 300);
    },
    saveRuleDraftNow() {
      if (this.draftRestoring || this.draftLoading || !this.tableData.length) {
        return;
      }
      const payload = {
        userId: this.getCurrentDraftUserId(),
        ruleId: this.ruleId,
        ruleName: this.ruleName,
        isLoadingFromTemplate: this.isLoadingFromTemplate,
        pageParams: this.pageParams,
        tableData: this.tableData,
        updatedAt: Date.now(),
      };
      localStorage.setItem(this.getRuleDraftKey(), JSON.stringify(payload));
    },
    restoreRuleDraft() {
      const draftKey = this.getRuleDraftKey();
      const legacyDraftKey = this.getLegacyRuleDraftKey();
      const rawDraft = localStorage.getItem(draftKey) || localStorage.getItem(legacyDraftKey);
      if (!rawDraft) {
        return;
      }
      try {
        const draft = JSON.parse(rawDraft);
        if (String(draft.ruleId) !== String(this.ruleId)) {
          return;
        }
        if (draft.userId && String(draft.userId) !== String(this.getCurrentDraftUserId())) {
          return;
        }
        this.draftRestoring = true;
        if (this.draftSaveTimer) {
          clearTimeout(this.draftSaveTimer);
          this.draftSaveTimer = null;
        }
        this.tableData = Array.isArray(draft.tableData) ? draft.tableData : this.tableData;
        this.isLoadingFromTemplate = !!draft.isLoadingFromTemplate;
        if (draft.pageParams) {
          this.pageParams = { ...this.pageParams, ...draft.pageParams };
        }
        if (!localStorage.getItem(draftKey)) {
          localStorage.setItem(draftKey, rawDraft);
        }
        if (legacyDraftKey !== draftKey) {
          localStorage.removeItem(legacyDraftKey);
        }
        this.$nextTick(() => {
          this.draftRestoring = false;
        });
      } catch (error) {
        localStorage.removeItem(draftKey);
        localStorage.removeItem(legacyDraftKey);
        this.draftRestoring = false;
      }
    },
    clearRuleDraft() {
      localStorage.removeItem(this.getRuleDraftKey());
      localStorage.removeItem(this.getLegacyRuleDraftKey());
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.saveRuleDraftNow();
      }
    },
    addCustomIndicator() {
      if (!this.canModifyCurrentRule) {
        this.$message.warning("该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。");
        return;
      }
      if (!this.isRootUser) {
        return;
      }
      const newRow = {
        category: "",
        description: "",
        standard: "",
        weight: 0,
        target: "",
        unit: "%", // 默认单位
      };
      this.tableData.push(newRow); // 添加到表格末尾
    },
    async loadCategoryOptions() {
      // Load all categories for display purposes
      // FIXED categories are needed to display names for fixed dimensions
      // but they will be filtered out in selectableCategoryOptions for user selection
      const response = await listDimensionCategories();
      if (response.data && response.data.code === "200") {
        this.categoryOptions = response.data.data || [];
        this.fixedCategoryMap = keyBy(
          response.data.data.filter(
            (i) => i.categoryType === CategoryType.FIXED,
          ),
          "id",
        );
      }
    },
    async loadDataSourceTypes() {
      try {
        const response = await fixedDimensionRequest.getDataSourceTypes();
        if (response.data && response.data.code === "200") {
          this.dataSourceTypes = response.data.data || [];
        }
      } catch (error) {
        console.error("加载数据源类型失败:", error);
      }
    },
    async handleCategoryChange(row, value) {
      if (!this.canModifyCurrentRule) {
        this.$message.warning("该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。");
        return;
      }
      if (!value) {
        row.categoryId = value;
        row.category = "";
        return;
      }
      const matched = this.categoryOptions.find((item) => item.id === value);
      if (matched) {
        row.categoryId = matched.id;
        row.category = matched.name;
        return;
      }
      if (!this.isSuperUser) {
        row.categoryId = null;
        row.category = "";
        this.$message.warning("只有超级管理员可以新增类别");
        return;
      }
      try {
        const response = await addDimensionCategory({
          name: value,
          isAiItem: false,
        });
        if (response.data.code === "200") {
          await this.loadCategoryOptions();
          const newCategory = this.categoryOptions.find(
            (item) => item.name === value,
          );
          if (newCategory) {
            row.categoryId = newCategory.id;
            row.category = newCategory.name;
          }
          this.$message.success("类别已创建");
        } else {
          this.$message.error(response.data.msg || "类别创建失败");
        }
      } catch (error) {
        console.error("创建类别失败:", error);
        this.$message.error("类别创建失败，请稍后重试");
      }
    },
    handleMobileRulesUpdate(rules) {
      if (!this.canModifyCurrentRule) {
        return;
      }
      this.tableData = rules.map((rule) => ({ ...rule }));
    },
    async handleMobileCategoryChange({ index, value }) {
      const row = this.sortedTableData[index];
      if (!row) {
        return;
      }
      await this.handleCategoryChange(row, value);
    },
    isCategoryAi(categoryId) {
      if (!categoryId) {
        return false;
      }
      const matched = this.categoryOptions.find(
        (item) => item.id === categoryId,
      );
      return !!(matched && matched.isAiItem);
    },
    isFixedDimension(row) {
      return row.isFixed === true;
    },
    getDataSourceDisplayName(sourceName) {
      const source = this.dataSourceTypes.find((s) => s.value === sourceName);
      return source ? source.displayName : sourceName;
    },
    getDataSourceDescription(sourceName) {
      const source = this.dataSourceTypes.find((s) => s.value === sourceName);
      return source ? source.description : "";
    },
    openTemplateLibrary() {
      this.tempParams.name = ""; // 清空搜索框
      this.selectedTemplates = []; // 清空选中状态
      this.tempParams.pageNum = 1;
      this.getTemplateList();
      this.templateDialogVisible = true;
    },
    async getTemplateList() {
      const response = await this.$http.get("/template/page", {
        params: {
          name: this.tempParams.name,
          pageNum: this.tempParams.pageNum,
          pageSize: this.tempParams.pageSize,
        },
      });
      if (response.data && response.data.code === "200") {
        this.tempParams.total = response.data.data.total;
        this.templateData = response.data.data.records;
      }
    },
    handleCurrentChange(val) {
      this.tempParams.pageNum = val;
      this.getTemplateList();
    },
    handleSizeChange(val) {
      this.tempParams.pageSize = val;
      this.tempParams.pageNum = 1;
      this.getTemplateList();
    },
    goBack() {
      this.$router.go(-1);
    },
    downloadTemplate() {
      try {
        const link = document.createElement("a");
        link.href = "/static/assessment_template.xlsx";
        link.download = "考核表导入模板.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.$message.success("模板下载成功");
      } catch (error) {
        console.error("模板下载失败:", error);
        this.$message.error("模板下载失败，请稍后重试");
      }
    },
    async confirmAddFromTemplate() {
      const response = await this.$http.get("/template/getDimensionsByTempId", {
        params: { tempId: this.queryTempId },
      });
      if (response.data && response.data.code === "200") {
        // Filter out fixed dimensions from the template - they should not be added from templates
        const nonFixedDimensions = response.data.data.filter(
          (item) => !item.isFixed
        );
        // Append the filtered dimensions to the current tableData instead of replacing
        const newDimensions = nonFixedDimensions.map((dimension) => ({
          ...dimension,
          ruleId: this.ruleId,
        }));
        this.tableData = [...this.tableData, ...newDimensions];
        this.pageParams.total += newDimensions.length;
        this.pageParams.pageSize = 100;
        this.pageParams.pageNum = 1;
        this.$message.success("从模板库添加成功，请保存以创建新规则");
        this.templateDialogVisible = false;
      }
    },
    handleTemplateSelection(val) {
      this.queryTempId = val.id;
    },
    beforeUpload(file) {
      const isLt3M = file.size / 1024 / 1024 < 3;
      if (!isLt3M) {
        this.$message.error("上传文件大小不能超过 3MB!");
        return false;
      }
      return isLt3M;
    },
    handleFileChange(file, fileList) {
      // 只保留最新选择的一个文件（配合limit=1使用）
      this.fileList = fileList.slice(-1);
    },
    async confirmImport() {
      if (!this.canModifyCurrentRule) {
        this.$message.warning("该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。");
        return;
      }
      if (this.fileList.length === 0) {
        this.$message.warning("请先上传文件");
        return;
      }
      const formData = new FormData();
      formData.append("file", this.fileList[0].raw);
      formData.append("ruleId", this.ruleId);
      const res = await this.$http.post("/dimension/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const data = res.data;
      if (data.code === "200") {
        this.$message.success("批量导入成功");
        this.batchImportDialogVisible = false;
        this.fileList = [];
        this.loadTableData();
      } else {
        this.$message.error(data.msg);
      }
    },
    handlePageChange(newPageNum) {
      this.pageParams.pageNum = newPageNum;
      this.loadTableData(); // 重新加载当前页数据
    },
    handlePageSizeChange(newPageSize) {
      this.pageParams.pageSize = newPageSize;
      this.pageParams.pageNum = 1; // 重置为第一页
      this.loadTableData();
    },

    async loadTableData() {
      this.draftLoading = true;
      this.permissionLoaded = false;
      try {
        const response = await this.$http.get("/dimension/page", {
          params: {
            ruleId: this.ruleId,
            pageNum: this.pageParams.pageNum,
            pageSize: this.pageParams.pageSize,
          },
        });
        console.log("loadTableData响应数据:", response);
        if (response.data.code === "200") {
          this.pageParams.total = response.data.data.dimensions.total;
          this.tableData = response.data.data.dimensions.records;
          // 从新响应结构中获取isTemplate值
          this.isLoadingFromTemplate = response.data.data.isTemplate || false;
          this.isRuleEditable = isEditableFlag(response.data.data.canEdit);
          this.permissionLoaded = true;
          this.restoreRuleDraft();
        }
      } finally {
        this.draftLoading = false;
      }
    },
    async saveData() {
      if (!this.canModifyCurrentRule) {
        this.$message.warning("该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。");
        return;
      }
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
      await this.$nextTick();

      const invalidItems = this.tableData.filter(
        (item) =>
          item.weight === null || item.weight === "" || item.weight <= 0,
      );
      if (invalidItems.length > 0) {
        this.$message.error("单项分值不能为0");
        return;
      }
      // 如果是从模板加载的数据，显示克隆规则命名对话框
      if (this.isLoadingFromTemplate && !this.isSuperUser) {
        this.cloneRuleForm.name = "";
        this.cloneRuleDialogVisible = true;
        return;
      }

      try {
        const response = await apiSaveDimension({
          ruleId: this.ruleId,
          dimensions: this.tableData,
        });
        if (response.data.code === "200") {
          this.clearRuleDraft();
          this.$message.success("保存成功");
          this.$router.go(-1);
        } else {
          this.$message.error(`保存失败: ${response.msg}`);
        }
      } catch (error) {
        console.error("保存数据出错:", error);
        this.$message.error("保存失败，请稍后重试");
      }
    },
    async confirmCloneRule() {
      this.$refs.cloneRuleForm.validate(async (valid) => {
        if (valid) {
          try {
            const response = await apiSaveDimension({
              ruleId: this.ruleId,
              dimensions: this.tableData,
              newRuleName: this.cloneRuleForm.name,
            });
            console.log("confirmCloneRule响应数据:", response);
            if (response.data.code === "200") {
              this.clearRuleDraft();
              this.$message.success("新规则创建成功");
              this.cloneRuleDialogVisible = false;
              this.isLoadingFromTemplate = false;
              // 更新当前规则ID为新创建的规则ID
              if (response.data.data && response.data.data.newRuleId) {
                this.ruleId = response.data.data.newRuleId;
              }
              this.$router.go(-1);
            } else {
              this.$message.error(`创建失败: ${response.data.msg}`);
            }
          } catch (error) {
            console.error("创建新规则出错:", error);
            this.$message.error("创建失败，请稍后重试");
          }
        }
      });
    },
    createFromTemplate() {
      if (!this.canModifyCurrentRule) {
        this.$message.warning("该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。");
        return;
      }
      this.batchImportDialogVisible = true;
    },
    saveAsTemplate() {
      this.templateForm = { name: "", description: "" };
      this.saveAsTemplateDialogVisible = true;
    },
    async confirmSaveAsTemplate() {
      this.$refs.templateForm.validate(async (valid) => {
        if (valid) {
          // 过滤掉固定考核指标，不将其保存到模板中
          const dimensionsToSave = this.tableData.filter(
            (item) => !this.isFixedDimension(item),
          );
          if (!dimensionsToSave.length) {
            this.$message.warning("没有可保存的维度");
            return;
          }
          const saveData = {
            name: this.templateForm.name,
            description: this.templateForm.description,
            dimensions: dimensionsToSave,
          };
          const response = await this.$http.post(
            "/template/saveTemplate",
            saveData,
          );
          console.info(response);
          if (response.data.code === "200") {
            this.$message.success("模板保存成功");
            this.saveAsTemplateDialogVisible = false;
          } else {
            this.$message.error("模板保存失败");
          }
        }
      });
    },
    handleDelete(index, row) {
      if (!this.canModifyCurrentRule) {
        this.$message.warning("该模板已进入审批流程，暂不可修改。如需调整，请由主管打回后再修改。");
        return;
      }
      if (!this.isRootUser) {
        this.$message.warning('无权限删除，仅 root 用户可用');
        return;
      }
      const originalIndex = this.tableData.findIndex(
        (item) => item === row || (row.id && item.id === row.id),
      );
      const deleteIndex = originalIndex >= 0 ? originalIndex : index;
      // Check if this is a template (template rows cannot be deleted)
      if (this.isLoadingFromTemplate) {
        return;
      }
      // Check if this is a fixed dimension
      if (this.isFixedDimension(row)) {
        this.$message.warning("固定考核指标不可删除");
        return;
      }
      this.$confirm(
        `确定要删除 ${row.categoryName || row.category || "该类别"} 吗?`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        },
      )
        .then(async () => {
          try {
            if (!row.id) {
              // 没有id，直接从tableData中删除
              this.tableData.splice(deleteIndex, 1);
              this.pageParams.total = Math.max(0, this.pageParams.total - 1);
              return; // 直接返回，不执行后续接口请求
            }
            const response = await this.$http.delete("/dimension/delete", {
              params: { dimensionId: row.id },
            });
            if (response.data.code === "200") {
              // 删除成功，从本地tableData中移除该行，避免丢失其他未保存的修改
              this.tableData.splice(deleteIndex, 1);
              this.pageParams.total = Math.max(0, this.pageParams.total - 1);
              this.$message.success("删除成功");
            } else {
              // 删除失败，重新加载表格数据以保持与后端一致
              this.$message.error(`删除失败: ${response.data.msg}`);
              this.loadTableData();
            }
          } catch (error) {
            console.error("删除失败:", error);
            this.$message.error("删除失败，请稍后重试");
          }
        })
        .catch(() => {
          // pass
        });
    },
    expandInput(scope, field) {
      const inputRef = this.$refs[`${field}Input`]?.[scope.$index];
      if (inputRef) {
        this.$set(this.expandedInputs, `${scope.$index}-${field}`, true);
        this.$nextTick(() => {
          inputRef.focus();
        });
      }
    },
    shrinkInput(scope, field) {
      this.$set(this.expandedInputs, `${scope.$index}-${field}`, false);
    },
    selectUnit(scope, unit) {
      this.$set(scope.row, "unit", unit);
    },
  },
};
</script>

<style scoped>
.assessment-table-container {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.left-actions {
  display: flex;
  align-items: center;
}

.right-actions {
  display: flex;
  gap: 10px;
}

.title {
  margin: 0 0 0 15px;
  font-size: 20px;
}

.back-button {
  font-size: 16px;
  padding: 0;
}

.save-button {
  margin-right: 10px;
}

.action-icon {
  width: 16px;
  height: 16px;
}

.table-container {
  flex: 1;
  border-radius: 4px;
}

.editable-input {
  transition: all 0.3s;
  width: 100%;
  height: 100%;
}

.editable-input.expanded-input {
  width: 300px !important;
  position: absolute;
  z-index: 100;
  background: white;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.target-input-container {
  display: flex;
  align-items: center;
}

.target-value-input {
  margin-right: 5px;
}

.unit-select-button {
  padding: 10px 5px;
  min-width: 40px;
}

.unit-selector {
  padding: 5px;
}

.unit-option {
  padding: 5px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.unit-option:hover {
  background-color: #f5f7fa;
}

.ok-icon {
  width: 12px;
  height: 12px;
  margin-left: 5px;
}

.indicator-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #409eff;
  font-size: 14px;
  flex-wrap: wrap;
  gap: 1rem;
}
.readonly-alert {
  margin-bottom: 10px;
}
.action-link {
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
  margin: 0 8px;
}
.action-link:hover {
  color: #2d8cf0;
}
.separator {
  color: #666;
}
.template-library {
  padding: 10px;
}
.dialog-footer {
  margin-top: 10px;
  text-align: right;
}
.category-select {
  width: 100%;
}

.category-cell {
  display: flex;
  align-items: center;
}

.category-select-wrapper {
  width: 100%;
  position: relative;
}

.selected-ai-tag {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
/* Fixed dimension tag styles */
.fixed-dimension-tag {
  background-color: #e6a23c;
  border: none;
  color: white;
  font-weight: 600;
  font-size: 11px;
  padding: 2px 6px;
  height: 18px;
  line-height: 14px;
  margin-left: 4px;
}

.category-manage-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Fixed dimension display with disabled style (80% opacity) */
.fixed-dimension-display {
  opacity: 0.8;
}
</style>
